/**
 * useRemotePositionTracker — Synthesizes store.position for remote renderer playback.
 *
 * WebAudioRenderer.handleTimeUpdate drives store.updateTime() by reading
 * audio.currentTime. For network renderers (UPnP, RAAT), the <audio> element
 * is paused, so handleTimeUpdate never fires and position stays at 0.
 *
 * When the active zone is a network renderer and playbackState is 'playing',
 * a 1-second interval increments position from a known anchor point — the same
 * approach iOS MPNowPlayingInfoCenter uses (set elapsed + rate, interpolate).
 *
 * This hook runs on ALL platforms (iPad, desktop, etc.) and only activates
 * when a network renderer is the active zone.
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { startUpnpStatePoll, stopUpnpStatePoll, getActivePollerZoneId } from '../transport/upnpStatePoller';
import { detectPlatform } from '../utils/platform';

function isNetworkRenderer(zone: any): boolean {
  if (!zone?.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

export function useRemotePositionTracker(): void {
  const anchorTimeRef = useRef(0);
  const anchorPositionRef = useRef(0);

  // Synthetic position ticker — runs when remote renderer is playing.
  // This ONLY drives the UI progress bar between ground-truth polls.
  // The upnpStatePoller corrects position every 3s from the real renderer;
  // this ticker fills the 1-second gaps so the progress bar is smooth.
  // Track advancement is handled by:
  //   - useRemoteAdvanceTimer (desktop/browser)
  //   - NativeQueueManager (iOS — native timer, not subject to WebView throttling)
  useEffect(() => {
    const interval = setInterval(() => {
      const s = useMaestroStore.getState();
      const isRemote = isNetworkRenderer(s.discovery?.activeZone);

      if (!isRemote || s.playbackState !== 'playing' || !s.currentTrack) {
        if (anchorTimeRef.current !== 0) anchorTimeRef.current = 0;
        return;
      }

      if (anchorTimeRef.current === 0) {
        anchorTimeRef.current = Date.now();
        anchorPositionRef.current = s.position || 0;
      }

      const elapsed = (Date.now() - anchorTimeRef.current) / 1000;
      const newPos = anchorPositionRef.current + elapsed;
      const duration = s.duration || 0;
      const clamped = duration > 0 ? Math.min(newPos, duration) : newPos;

      // Only tick forward by 1s increments — if the poller just corrected
      // position (jump > 2s from expected), re-anchor instead of overwriting
      const currentStorePos = s.position;
      const expectedDelta = Math.abs(clamped - currentStorePos);
      if (expectedDelta > 3) {
        // Store was corrected by the UPnP poller — re-anchor to current store value
        anchorTimeRef.current = Date.now();
        anchorPositionRef.current = currentStorePos;
        return;
      }

      if (Math.floor(clamped) !== Math.floor(currentStorePos)) {
        s.updateTime(clamped, duration);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // UPnP ground-truth state poller — detects drift between Zustand and actual renderer state.
  // Disabled on native platforms (iOS + Tauri desktop): the native bridge / Connector
  // handles renderer communication. Browser fetch() to http:// LAN IPs is blocked by
  // mixed-content policy when the app is served over HTTPS, flooding the console with errors.
  // Also skip RAAT devices (raat-uuid: prefix) — they don't speak UPnP SOAP.
  useEffect(() => {
    const plat = detectPlatform();
    if (plat.isIOSNativeApp || plat.isTauriDesktop) return;

    const unsubPoller = useMaestroStore.subscribe((state) => {
      const zone = state.discovery?.activeZone as any;
      const isRemote = isNetworkRenderer(zone);
      const zoneId = zone?.id || '';
      const isRaat = typeof zoneId === 'string' && zoneId.startsWith('raat-uuid:');
      const transportType = (zone?.metadata?.transport_type || zone?.transport_type || '') as string;
      const isUpnp = !isRaat && (transportType === 'upnp' || zone?.type === 'upnp'
        || (isRemote && !transportType));
      const isPlaying = state.playbackState === 'playing';

      if (isRemote && isUpnp && isPlaying) {
        if (getActivePollerZoneId() !== zone?.id) {
          startUpnpStatePoll(zone);
        }
      } else {
        if (getActivePollerZoneId()) {
          stopUpnpStatePoll();
        }
      }
    });

    return () => {
      unsubPoller();
      stopUpnpStatePoll();
    };
  }, []);

  // Detect seek (position jump > 3s) and track changes to reset anchor
  useEffect(() => {
    let lastTrackId: string | null = null;
    let lastPosition = 0;

    const unsubscribe = useMaestroStore.subscribe((state) => {
      const trackId = state.currentTrack?.id || null;

      if (trackId !== lastTrackId) {
        lastTrackId = trackId;
        anchorTimeRef.current = 0;
        anchorPositionRef.current = 0;
        lastPosition = 0;
        return;
      }

      const delta = Math.abs(state.position - lastPosition);
      if (delta > 3 && anchorTimeRef.current !== 0) {
        anchorTimeRef.current = Date.now();
        anchorPositionRef.current = state.position;
      }

      lastPosition = state.position;
    });

    return unsubscribe;
  }, []);
}
